import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLines } from "@/components/Reveal";

const STRATEGY_IMG =
  "https://images.pexels.com/photos/5520284/pexels-photo-5520284.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GENEVA_IMG =
  "https://images.pexels.com/photos/17108143/pexels-photo-17108143.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Services() {
  const { t } = useTranslation("services");
  const pillars = t("pillars.items", { returnObjects: true });
  const practice = t("practice.items", { returnObjects: true });
  const lending = t("lending.items", { returnObjects: true });
  const commitments = t("pe.commitments", { returnObjects: true });
  const cells = t("structures.cells", { returnObjects: true });
  const financing = t("financing.items", { returnObjects: true });
  const leadLender = t("leadLender.items", { returnObjects: true });

  return (
    <div data-testid="services-page" className="pt-32 lg:pt-40">
      <header className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <p className="overline-label mb-8 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-brass/60" />
          {t("hero.overline")}
        </p>
        <h1 className="max-w-3xl font-display text-4xl font-light leading-[1.08] tracking-tight text-chalk sm:text-5xl lg:text-6xl">
          <MaskedLines
            lines={[
              t("hero.titleA"),
              <span key="b" className="font-serif italic text-brass">{t("hero.titleB")}</span>,
            ]}
          />
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-mist/85">{t("hero.lead")}</p>
      </header>

      <section className="mx-auto mt-16 max-w-7xl px-6 sm:px-8 lg:px-12 lg:mt-20">
        <Reveal>
          <p className="overline-label">{t("pillars.overline")}</p>
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
            {t("pillars.titleA")}
            <span className="font-serif italic text-brass">{t("pillars.titleB")}</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-px bg-white/[0.06] lg:grid-cols-3">
          {pillars.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08} className="bg-ink">
              <article
                data-testid={`service-pillar-${s.id}`}
                className="group flex h-full flex-col p-8 transition-colors duration-300 hover:bg-elevated lg:p-10"
              >
                <p className="font-mono text-xs tracking-[0.24em] text-brass/70">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-5 font-display text-xl text-chalk">{s.title}</h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-slate2">{s.description}</p>
                <Link
                  to="/contact"
                  data-testid={`service-pillar-link-${s.id}`}
                  className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brass transition-colors duration-200 hover:text-chalk"
                >
                  {t("pillars.learnMore")} <ArrowUpRight size={14} />
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 sm:px-8 lg:px-12">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-8 border border-brass/25 bg-elevated/40 p-8 lg:flex-row lg:items-center lg:p-12">
            <div>
              <h2 className="font-display text-2xl font-light tracking-tight text-chalk sm:text-3xl">
                {t("ctaBand.titleA")}
                <span className="font-serif italic text-brass">{t("ctaBand.titleB")}</span>
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-mist/85">{t("ctaBand.lead")}</p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-4">
              <Link to="/contact" data-testid="services-cta-appointment" className="btn-brass">
                {t("ctaBand.btnPrimary")} <ArrowUpRight size={15} />
              </Link>
              <Link to="/investissements" data-testid="services-cta-expertise" className="btn-ghost">
                {t("ctaBand.btnSecondary")}
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal>
            <p className="overline-label">{t("practice.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
              {t("practice.title")}
            </h2>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("practice.lead")}</p>
          </Reveal>
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {practice.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.06}>
                <article
                  data-testid={`practice-area-${a.id}`}
                  className="h-full border border-white/[0.08] bg-ink p-8 transition-colors duration-300 hover:border-brass/35 lg:p-10"
                >
                  <h3 className="font-display text-xl text-chalk">{a.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-mist/80">{a.description}</p>
                  <ul className="mt-8 grid gap-3 border-t border-white/[0.08] pt-6 sm:grid-cols-2">
                    {a.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-sm text-mist/80">
                        <Check size={15} className="mt-0.5 shrink-0 text-brass" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-slate2/70">
                    {t("practice.note")}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <p className="overline-label">{t("lending.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("lending.titleA")}
              <span className="font-serif italic text-brass">{t("lending.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-mist/85">{t("lending.lead")}</p>
            <div className="mt-10 space-y-0">
              {lending.map((p) => (
                <div key={p.num} className="grid gap-2 border-t border-white/[0.08] py-6 last:border-b sm:grid-cols-[56px_1fr] sm:gap-8">
                  <p className="font-mono text-xs tracking-[0.24em] text-brass/70">{p.num}</p>
                  <div>
                    <h3 className="font-display text-lg text-chalk">{p.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate2">{p.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="overflow-hidden border border-white/10">
              <img
                src={STRATEGY_IMG}
                alt={t("lending.imgAlt")}
                className="h-80 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[460px]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
              <p className="overline-label">{t("pe.overline")}</p>
              <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
                {t("pe.titleA")}
                <span className="font-serif italic text-brass">{t("pe.titleB")}</span>
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-mist/85">{t("pe.para1")}</p>
              <p className="mt-4 text-sm leading-relaxed text-slate2">{t("pe.para2")}</p>
            </Reveal>
            <div className="lg:col-span-7">
              {commitments.map((c, i) => (
                <Reveal key={c} delay={i * 0.06}>
                  <div className="flex items-baseline gap-6 border-t border-white/[0.08] py-6 last:border-b">
                    <span className="font-mono text-xs tracking-[0.24em] text-brass/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="font-display text-lg text-chalk">{c}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <Reveal>
          <p className="overline-label">{t("structures.overline")}</p>
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
            {t("structures.titleA")}
            <span className="font-serif italic text-brass">{t("structures.titleB")}</span>
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="space-y-5 text-sm leading-relaxed text-mist/85">
              <p>{t("structures.para1")}</p>
              <p className="text-slate2">{t("structures.para2")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <dl className="grid grid-cols-3 gap-px bg-white/[0.06]">
              {cells.map((c) => (
                <div key={c.label} className="bg-ink p-5">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate2">{c.label}</dt>
                  <dd className="mt-3 font-display text-base text-brass">{c.value}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
          <Reveal>
            <p className="overline-label">{t("financing.overline")}</p>
            <h2 className="mt-4 max-w-2xl font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("financing.titleA")}
              <span className="font-serif italic text-brass">{t("financing.titleB")}</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-5">
            {financing.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.05} className="bg-ink">
                <div
                  data-testid={`financing-solution-${i + 1}`}
                  className="h-full p-6 transition-colors duration-300 hover:bg-elevated"
                >
                  <p className="font-mono text-xs tracking-[0.24em] text-brass/70">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-4 font-display text-base text-chalk">{f.title}</h3>
                  <p className="mt-3 text-xs leading-relaxed text-slate2">{f.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <Reveal>
          <p className="overline-label">{t("leadLender.overline")}</p>
          <h2 className="mt-4 max-w-3xl font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
            {t("leadLender.titleA")}
            <span className="font-serif italic text-brass">{t("leadLender.titleB")}</span>
          </h2>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate2">{t("leadLender.lead")}</p>
        </Reveal>
        <div className="mt-12 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
          {leadLender.map((a, i) => (
            <Reveal key={a.num} delay={i * 0.06} className="bg-ink">
              <div className="h-full p-8 transition-colors duration-300 hover:bg-elevated">
                <p className="font-mono text-xs tracking-[0.24em] text-brass/70">{a.num}</p>
                <h3 className="mt-4 font-display text-lg text-chalk">{a.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate2">{a.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-28">
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <div className="overflow-hidden border border-white/10">
                <img
                  src={GENEVA_IMG}
                  alt={t("final.imgAlt")}
                  className="h-64 w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-8">
              <h2 className="font-display text-3xl font-light leading-[1.15] tracking-tight text-chalk sm:text-4xl">
                {t("final.titleA")}
                <span className="font-serif italic text-brass">{t("final.titleB")}</span>
              </h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-mist/85">{t("final.lead")}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid="services-final-cta-appointment" className="btn-brass">
                  {t("final.btnPrimary")} <ArrowUpRight size={15} />
                </Link>
                <Link to="/investissements" data-testid="services-final-cta-process" className="btn-ghost">
                  {t("final.btnSecondary")}
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
